import type { RepaymentBasis } from '../../../lib/calc/repaymentBasis';
import { deriveOtherLever } from '../../../lib/calc/repaymentBasis';

/**
 * ============================================================================
 * REPAYMENT CONSEQUENCE — pure state-machine logic
 * ============================================================================
 *
 * Extracted from RepaymentConsequenceFields.tsx so the exact live-
 * recalculation / no-ping-pong rules can be unit-tested directly (via
 * `tsx`, consistent with this project's existing testing approach) without
 * needing a React testing-library dependency. This file contains NO React
 * imports and NO JSX — it is pure data-in/data-out logic.
 *
 * THE STATE MACHINE (approved spec):
 *
 * NEW EVENT (hasUserManuallyEdited starts false):
 *   - While hasUserManuallyEdited is false, BOTH emi and tenure are
 *     re-derived from the CURRENT basis every time basis changes — tenure
 *     from basis.remainingTenureMonths directly, EMI from tenure via
 *     deriveOtherLever. Neither value is "set by you" yet.
 *   - The moment the user edits either field directly, that field becomes
 *     the primary lever, hasUserManuallyEdited becomes true, and the
 *     OTHER field is derived from it via deriveOtherLever.
 *   - From that point forward, further basis changes (new rate typed,
 *     prepayment amount changed, etc.) recalculate ONLY the non-primary
 *     field from the CURRENT primary value — the primary field itself is
 *     never touched by a basis change, only by the user editing it again
 *     directly. This is what prevents ping-pong.
 *
 * EXISTING EVENT (hasUserManuallyEdited starts true when a saved
 * consequence is passed in at construction time):
 *   - The saved newEmi/newRemainingTenureMonths/primaryLever load as-is.
 *   - No live recalculation happens on mount merely because the current
 *     basis differs from what was true when the event was originally
 *     saved — the saved values are authoritative until the user manually
 *     edits one of them, at which point the normal primary-lever rule
 *     applies exactly as for a new event.
 */

export type PrimaryLever = 'emi' | 'tenure';

export interface ConsequenceDisplayState {
  emi: number | undefined;
  tenure: number | undefined;
  primaryLever: PrimaryLever;
  /**
   * True once the user has manually edited EITHER field at least once.
   * While false, both fields are considered "automatically calculated"
   * for display/labelling purposes (never "Set by you"), even though
   * `primaryLever` internally defaults to 'tenure' to pick a calculation
   * direction — primaryLever alone must NEVER be read as "the user set
   * this field"; hasUserManuallyEdited is the only source of truth for
   * that distinction.
   */
  hasUserManuallyEdited: boolean;
}

/**
 * The initial state for a BRAND-NEW event (no previously saved
 * consequence). Both fields start undefined; the caller is expected to
 * call `recalculateFromBasis` once a usable basis is available (see
 * RepaymentConsequenceFields.tsx's effect) to populate them for the
 * first time.
 */
export function initialStateForNewEvent(): ConsequenceDisplayState {
  return {
    emi: undefined,
    tenure: undefined,
    primaryLever: 'tenure',
    hasUserManuallyEdited: false,
  };
}

/**
 * The initial state for an EXISTING event that already has a saved
 * consequence. Loads the saved values as-is and marks the state as
 * already user-edited, so no live recalculation happens on mount.
 */
export function initialStateForExistingConsequence(saved: {
  newEmi?: number;
  newRemainingTenureMonths?: number;
  primaryLever: PrimaryLever;
}): ConsequenceDisplayState {
  return {
    emi: saved.newEmi,
    tenure: saved.newRemainingTenureMonths,
    primaryLever: saved.primaryLever,
    hasUserManuallyEdited: true,
  };
}

/**
 * Called whenever the basis changes (new rate typed, prepayment amount
 * changed, or any other upstream input affecting computeRepaymentBasis's
 * result) AND whenever the component wants to (re)derive a value from
 * the current primary lever. Behaviour depends entirely on
 * hasUserManuallyEdited:
 *
 *   - If the user has NOT yet manually edited either field: the
 *     AUTOMATIC branch runs, controlled by `keepEmiFixed` (the explicit
 *     "Keep current EMI" checkbox — see below). This checkbox is NEVER
 *     itself a manual edit; toggling it never sets hasUserManuallyEdited.
 *       - keepEmiFixed = false (default, unchecked): tenure is held at
 *         basis.remainingTenureMonths (the latest carried-forward
 *         trajectory), EMI is recalculated from that fixed tenure at the
 *         new rate/principal. This is the existing default behaviour,
 *         unchanged.
 *       - keepEmiFixed = true (checked): EMI is held at
 *         `currentActiveEmi` (the EMI actually in effect right now,
 *         BEFORE this event — passed in by the caller from the basis
 *         calculation's own resolution, never re-derived here), and
 *         remaining tenure is recalculated from that fixed EMI at the
 *         new rate/principal.
 *   - If the user HAS manually edited one field: unchanged from before —
 *     that field (per current.primaryLever) is left completely
 *     untouched; only the OTHER field is recalculated. `keepEmiFixed` is
 *     ignored entirely once a manual edit has occurred, since the user's
 *     own choice of primary lever now governs — this mirrors the
 *     existing rule that hasUserManuallyEdited fully supersedes the
 *     automatic phase.
 *
 * @param currentActiveEmi The EMI actually in effect right now, before
 *   this event, as already resolved by the basis calculation (e.g.
 *   basis.applicableAnnualRatePercent's companion EMI figure — the
 *   caller supplies this since it already has it; this function does
 *   not re-derive it). Only read when keepEmiFixed is true and the
 *   automatic branch is active.
 *
 * Returns a new ConsequenceDisplayState; never mutates `current`.
 */
export function recalculateFromBasis(
  current: ConsequenceDisplayState,
  basis: Pick<RepaymentBasis, 'outstandingPrincipal' | 'applicableAnnualRatePercent' | 'remainingTenureMonths'>,
  keepEmiFixed: boolean = false,
  currentActiveEmi?: number
): ConsequenceDisplayState {
  if (!current.hasUserManuallyEdited) {
    if (basis.outstandingPrincipal <= 0 || basis.remainingTenureMonths <= 0) {
      // Not enough information yet to suggest anything — leave blank
      // rather than showing a nonsensical calculated value.
      return { ...current, emi: undefined, tenure: undefined };
    }

    if (keepEmiFixed && currentActiveEmi !== undefined) {
      // "Keep current EMI" checked: hold EMI fixed at the currently
      // active value, recalculate tenure from it at the new rate/principal.
      const derivedTenure = deriveOtherLever(basis, 'emi', currentActiveEmi);
      return {
        ...current,
        emi: currentActiveEmi,
        tenure: derivedTenure,
        primaryLever: 'emi',
      };
    }

    // Default (unchecked): hold tenure fixed at the latest carried-
    // forward trajectory, recalculate EMI from it — unchanged behaviour.
    const suggestedEmi = deriveOtherLever(basis, 'tenure', basis.remainingTenureMonths);
    return {
      ...current,
      tenure: basis.remainingTenureMonths,
      emi: suggestedEmi,
      primaryLever: 'tenure',
    };
  }

  // User has taken over — only the NON-primary field is recalculated,
  // derived from whatever the user's own primary value currently is.
  // The primary field itself is returned unchanged. keepEmiFixed is
  // irrelevant here — the user's own manual choice governs.
  if (current.primaryLever === 'emi') {
    const derivedTenure = current.emi !== undefined ? deriveOtherLever(basis, 'emi', current.emi) : undefined;
    return { ...current, tenure: derivedTenure };
  }
  const derivedEmi = current.tenure !== undefined ? deriveOtherLever(basis, 'tenure', current.tenure) : undefined;
  return { ...current, emi: derivedEmi };
}

/**
 * Called when the user directly edits the EMI field. Marks EMI as the
 * new primary lever, marks the state as user-edited from now on, and
 * derives tenure from the newly-entered EMI against the CURRENT basis.
 */
export function applyManualEmiEdit(
  newEmi: number | undefined,
  basis: Pick<RepaymentBasis, 'outstandingPrincipal' | 'applicableAnnualRatePercent'>
): ConsequenceDisplayState {
  const derivedTenure = newEmi !== undefined ? deriveOtherLever(basis, 'emi', newEmi) : undefined;
  return {
    emi: newEmi,
    tenure: derivedTenure,
    primaryLever: 'emi',
    hasUserManuallyEdited: true,
  };
}

/**
 * Called when the user directly edits the Remaining Tenure field.
 * Mirrors applyManualEmiEdit for the tenure lever.
 */
export function applyManualTenureEdit(
  newTenure: number | undefined,
  basis: Pick<RepaymentBasis, 'outstandingPrincipal' | 'applicableAnnualRatePercent'>
): ConsequenceDisplayState {
  const derivedEmi = newTenure !== undefined ? deriveOtherLever(basis, 'tenure', newTenure) : undefined;
  return {
    emi: derivedEmi,
    tenure: newTenure,
    primaryLever: 'tenure',
    hasUserManuallyEdited: true,
  };
}
